/**
 * State 1 — Free user, Skills Assessment NOT completed.
 * Goal: get the user to take their free Skills Assessment immediately.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DashboardUser } from './types';
import { HeroBanner } from './shared/HeroBanner';
import { UpgradeCard } from './shared/UpgradeCard';
import { TodayGoal, Goal } from './shared/TodayGoal';
import { ROUTES } from '../../utils/constants';
import { courseService } from '../../services/firebase/courses';
import { Course } from '../../types/course';

interface Props {
  user: DashboardUser;
}

const HOW_IT_WORKS = [
  {
    n: '1',
    title: 'Answer a few quick questions',
    desc: 'Tell us about your role, goals, and current AI experience — no jargon required.',
  },
  {
    n: '2',
    title: 'Get your personalised plan',
    desc: 'We match you with the right courses based on exactly where you are right now.',
  },
  {
    n: '3',
    title: 'Learn in 15-minute bites',
    desc: 'Practical, jargon-free lessons you can fit into any busy schedule.',
  },
];

export const DashState1NoAssessment: React.FC<Props> = ({ user }) => {
  const [freeCourses, setFreeCourses] = useState<Course[]>([]);

  useEffect(() => {
    courseService
      .getCourses({ status: 'published' })
      .then(({ courses }) => {
        setFreeCourses(courses.filter(c => c.isFree).slice(0, 3));
      })
      .catch(() => setFreeCourses([]));
  }, []);

  const exploredFreeCourse = user.enrolledCourses.some(c => c.isFree);

  const goals: Goal[] = [
    // assessmentCompleted will always be false in State 1 by definition, but logic is in place
    { label: 'Take your Skills Assessment', done: user.assessmentCompleted },
    { label: 'Explore a free course', done: exploredFreeCourse },
    // TODO: wire to community posts when data is available (no existing query for user posts)
    { label: 'Join the community', done: false },
  ];

  return (
    <div style={{ background: '#f7f8ff', minHeight: '100%' }}>
      <main className="container-custom py-10">
        {/* Hero */}
        <HeroBanner
          headline={`Hey ${user.name}, let's find your starting point.`}
          sub="Your free Skills Assessment takes just 10 minutes and gives you a personalised learning plan built around your goals. No tech background required."
          animDelay={0}
        />

        {/* Mobile: Today's Goals immediately below hero */}
        <div className="block lg:hidden mb-6">
          <TodayGoal goals={goals} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ── LEFT COLUMN (2/3) ────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">
            {/* ── PRIMARY CTA CARD ─────────────────────────────────────── */}
            <div
              className="db-fade-up rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6"
              style={{
                background: 'linear-gradient(135deg, #5b6cff, #7c8eff)',
                boxShadow: '0 4px 24px rgba(91,108,255,0.25)',
                animationDelay: '100ms',
              }}
            >
              <div className="flex items-center gap-5">
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0"
                  style={{ background: 'rgba(255,255,255,0.18)' }}
                >
                  🎯
                </div>
                <div>
                  <h2 className="font-montserrat font-extrabold text-2xl text-white mb-1">
                    Start your free Skills Assessment
                  </h2>
                  <p className="font-open-sans text-sm text-white opacity-85 max-w-md leading-relaxed">
                    10 minutes · Personalised results · No tech knowledge needed
                  </p>
                </div>
              </div>

              <Link
                to={ROUTES.skillsAssessment}
                className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-montserrat font-bold text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
                style={{
                  background: '#fff',
                  color: '#5b6cff',
                  boxShadow: '0 4px 18px rgba(0,0,0,0.12)',
                }}
              >
                Begin Assessment →
              </Link>
            </div>

            {/* ── SECONDARY UPGRADE CARD ───────────────────────────────── */}
            <div style={{ animationDelay: '150ms' }}>
              <UpgradeCard />
            </div>

            {/* Two-column: How It Works + Social Proof */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* ── HOW IT WORKS ─────────────────────────────────────────── */}
              <div
                className="db-fade-up rounded-2xl p-6 bg-white"
                style={{ border: '1.5px solid #e8e8ee', animationDelay: '200ms' }}
              >
                <h3 className="font-montserrat font-bold text-base text-neutral-800 mb-5">
                  How it works
                </h3>
                <div className="space-y-5">
                  {HOW_IT_WORKS.map((step) => (
                    <div key={step.n} className="flex items-start gap-4">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center font-montserrat font-bold text-sm text-white flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #5b6cff, #7c8eff)' }}
                      >
                        {step.n}
                      </div>
                      <div>
                        <p className="font-montserrat font-bold text-sm text-neutral-800 mb-0.5">
                          {step.title}
                        </p>
                        <p className="font-open-sans text-sm text-neutral-600 leading-relaxed">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── SOCIAL PROOF ─────────────────────────────────────────── */}
              <div
                className="db-fade-up rounded-2xl p-6 flex flex-col justify-between"
                style={{
                  background: 'linear-gradient(135deg, #1e1e2e, #2d2d42)',
                  border: '1.5px solid rgba(255,255,255,0.06)',
                  animationDelay: '300ms',
                }}
              >
                <div>
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} style={{ color: '#f59e0b', fontSize: '18px' }}>★</span>
                    ))}
                  </div>
                  <blockquote className="font-open-sans text-base text-neutral-200 leading-relaxed mb-5 italic">
                    "I did the Skills Assessment on a Monday and by Wednesday I had automated
                    two tasks I'd been doing manually for years."
                  </blockquote>
                </div>
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center font-montserrat font-bold text-sm text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #5b6cff, #7c8eff)' }}
                  >
                    S
                  </div>
                  <div>
                    <p className="font-montserrat font-bold text-sm text-white">Sarah J.</p>
                    <p className="font-open-sans text-xs text-neutral-400">Freelance Writer · UK</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ── FREE COURSE TEASER ───────────────────────────────────── */}
            {freeCourses.length > 0 && (
              <div
                className="db-fade-up rounded-2xl p-6 bg-white"
                style={{ border: '1.5px solid #e8e8ee', animationDelay: '400ms' }}
              >
                <h3 className="font-montserrat font-bold text-base text-neutral-800 mb-5">
                  Or explore free courses while you're here
                </h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {freeCourses.map((course) => (
                    <Link
                      key={course.id}
                      to={`/courses/${course.id}`}
                      className="flex items-start gap-3 p-4 rounded-xl border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md group"
                      style={{ border: '1.5px solid #e8e8ee' }}
                    >
                      <div className="flex-shrink-0">
                        {course.thumbnail ? (
                          <img
                            src={course.thumbnail}
                            alt={course.title}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="text-2xl">📚</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-montserrat font-bold text-sm text-neutral-800 leading-snug mb-1 group-hover:text-primary transition-colors">
                          {course.title}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-open-sans text-xs text-neutral-500">
                            {course.estimatedDuration} min
                          </span>
                          <span
                            className="px-2 py-0.5 rounded-full font-montserrat font-bold text-xs text-white"
                            style={{
                              background: 'linear-gradient(135deg, #00b89a, #00c2a8)',
                              fontSize: '10px',
                            }}
                          >
                            Free
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT SIDEBAR (1/3) ──────────────────────────────────── */}
          <div className="space-y-5">
            {/* Today's Goals — first card in sidebar, hidden on mobile (shown above hero on mobile) */}
            <div className="db-fade-up hidden lg:block" style={{ animationDelay: '100ms' }}>
              <TodayGoal goals={goals} />
            </div>
          </div>
        </div>
        {/* ── PRIMARY CTA CARD ─────────────────────────────────────── */}
        <div
          className="db-fade-up rounded-2xl p-8 md:p-10 mb-8 flex flex-col md:flex-row items-center justify-between gap-6"
          style={{
            background: 'linear-gradient(135deg, #5b6cff, #7c8eff)',
            boxShadow: '0 4px 24px rgba(91,108,255,0.25)',
            animationDelay: '100ms',
          }}
        >
          <div className="flex items-center gap-5">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.18)' }}
            >
              🎯
            </div>
            <div>
              <h2 className="font-montserrat font-extrabold text-2xl text-white mb-1">
                Start your free Skills Assessment
              </h2>
              <p className="font-open-sans text-sm text-white opacity-85 max-w-md leading-relaxed">
                10 minutes · Personalised results · No tech knowledge needed
              </p>
            </div>
          </div>

          <Link
            to={ROUTES.skillsAssessment}
            className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-montserrat font-bold text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl"
            style={{
              background: '#fff',
              color: '#5b6cff',
              boxShadow: '0 4px 18px rgba(0,0,0,0.12)',
            }}
          >
            Begin Assessment →
          </Link>
        </div>

        {/* ── SECONDARY UPGRADE CARD ───────────────────────────────── */}
        <div className="mb-8" style={{ animationDelay: '150ms' }}>
          <UpgradeCard />
        </div>

        {/* Two-column: How It Works + Social Proof */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* ── HOW IT WORKS ─────────────────────────────────────────── */}
          <div
            className="db-fade-up rounded-2xl p-6 bg-white"
            style={{ border: '1.5px solid #e8e8ee', animationDelay: '200ms' }}
          >
            <h3 className="font-montserrat font-bold text-base text-neutral-800 mb-5">
              How it works
            </h3>
            <div className="space-y-5">
              {HOW_IT_WORKS.map((step) => (
                <div key={step.n} className="flex items-start gap-4">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center font-montserrat font-bold text-sm text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #5b6cff, #7c8eff)' }}
                  >
                    {step.n}
                  </div>
                  <div>
                    <p className="font-montserrat font-bold text-sm text-neutral-800 mb-0.5">
                      {step.title}
                    </p>
                    <p className="font-open-sans text-sm text-neutral-600 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── SOCIAL PROOF ─────────────────────────────────────────── */}
          <div
            className="db-fade-up rounded-2xl p-6 flex flex-col justify-between"
            style={{
              background: 'linear-gradient(135deg, #1e1e2e, #2d2d42)',
              border: '1.5px solid rgba(255,255,255,0.06)',
              animationDelay: '300ms',
            }}
          >
            <div>
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} style={{ color: '#f59e0b', fontSize: '18px' }}>★</span>
                ))}
              </div>
              <blockquote className="font-open-sans text-base text-neutral-200 leading-relaxed mb-5 italic">
                "I did the Skills Assessment on a Monday and by Wednesday I had automated
                two tasks I'd been doing manually for years."
              </blockquote>
            </div>
            <div className="flex items-center gap-3">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-montserrat font-bold text-sm text-white flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #5b6cff, #7c8eff)' }}
              >
                S
              </div>
              <div>
                <p className="font-montserrat font-bold text-sm text-white">Sarah J.</p>
                <p className="font-open-sans text-xs text-neutral-400">Freelance Writer · UK</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── FREE COURSE TEASER ───────────────────────────────────── */}
        {freeCourses.length > 0 && (
          <div
            className="db-fade-up rounded-2xl p-6 bg-white"
            style={{ border: '1.5px solid #e8e8ee', animationDelay: '400ms' }}
          >
            <h3 className="font-montserrat font-bold text-base text-neutral-800 mb-5">
              Or explore free courses while you're here
            </h3>
            <div className="grid sm:grid-cols-3 gap-4">
              {freeCourses.map((course) => (
                <Link
                  key={course.id}
                  to={`/courses/${course.id}`}
                  className="flex items-start gap-3 p-4 rounded-xl border transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md group"
                  style={{ border: '1.5px solid #e8e8ee' }}
                >
                  <div className="flex-shrink-0">
                    {course.thumbnail ? (
                      <img
                        src={course.thumbnail}
                        alt={course.title}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="text-2xl">📚</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-montserrat font-bold text-sm text-neutral-800 leading-snug mb-1 group-hover:text-primary transition-colors">
                      {course.title}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-open-sans text-xs text-neutral-500">
                        {course.estimatedDuration} min
                      </span>
                      <span
                        className="px-2 py-0.5 rounded-full font-montserrat font-bold text-xs text-white"
                        style={{
                          background: 'linear-gradient(135deg, #00b89a, #00c2a8)',
                          fontSize: '10px',
                        }}
                      >
                        Free
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
